import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { requestService } from '../services/requestService';
import { toast } from 'sonner';
import { companyInfo } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await requestService.createRequest({
        nomClient: formData.nom,
        prenomClient: formData.prenom,
        telephoneClient: formData.telephone,
        emailClient: formData.email,
        message: formData.message,
        typeDemande: 'Contact'
      });

      toast.success('Message envoyé ! Nous vous contacterons très bientôt.');

      setFormData({ nom: '', prenom: '', telephone: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: Phone,
      title: 'Téléphone 1',
      info: companyInfo.phone,
      link: `tel:${companyInfo.phone}`,
      description: 'Appelez-nous pour un conseil immédiat'
    },
    {
      icon: Phone,
      title: 'Téléphone 2',
      info: companyInfo.phone2,
      link: `tel:${companyInfo.phone2}`,
      description: 'Ligne alternative disponible'
    },
    {
      icon: Mail,
      title: 'Email',
      info: companyInfo.email,
      link: `mailto:${companyInfo.email}`,
      description: 'Envoyez-nous un email'
    },
    {
      icon: MapPin,
      title: 'Adresse',
      info: companyInfo.address,
      link: null,
      description: 'Venez nous rendre visite'
    },
    {
      icon: Clock,
      title: 'Horaires',
      info: 'Lun - Ven: 8h - 18h',
      link: null,
      description: 'Sam: 9h - 13h'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.8)), url('https://images.unsplash.com/photo-1756435292384-1bf32eff7baf')`
          }}
        />
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Contactez-nous</h1>
          <p className="text-xl max-w-2xl mx-auto text-slate-200">
            Notre équipe est à votre disposition pour répondre à toutes vos questions
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-600 transition-colors">
                    <method.icon className="w-8 h-8 text-amber-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{method.title}</h3>
                  {method.link ? (
                    <a
                      href={method.link}
                      className="text-amber-600 hover:text-amber-700 font-medium mb-1 block"
                    >
                      {method.info}
                    </a>
                  ) : (
                    <p className="text-slate-700 font-medium mb-1">{method.info}</p>
                  )}
                  <p className="text-sm text-slate-600">{method.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Form */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Envoyez-nous un message
              </h2>
              <p className="text-slate-600 mb-8">
                Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais.
              </p>
              <Card>
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="prenom">Prénom *</Label>
                      <Input
                        id="prenom"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        required
                        placeholder="Votre prénom"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="nom">Nom *</Label>
                      <Input
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                        placeholder="Votre nom"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="telephone">Téléphone</Label>
                      <Input
                        id="telephone"
                        name="telephone"
                        type="tel"
                        value={formData.telephone}
                        onChange={handleChange}
                        placeholder="+221 XX XXX XX XX"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="votre@email.com"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Décrivez votre projet ou posez votre question..."
                        rows={6}
                        className="mt-2"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-lg py-6"
                    >
                      {isSubmitting ? (
                        'Envoi en cours...'
                      ) : (
                        <>
                          Envoyer le message
                          <Send className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Info */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Pourquoi nous contacter ?
              </h2>
              <p className="text-slate-600 mb-8">
                Nos experts sont là pour vous accompagner dans tous vos projets immobiliers.
              </p>
              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                      Consultation gratuite
                    </h3>
                    <p className="text-slate-600">
                      Bénéficiez d'une consultation gratuite avec nos experts pour évaluer vos besoins 
                      et définir la meilleure stratégie pour votre projet immobilier.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                      Réponse rapide
                    </h3>
                    <p className="text-slate-600">
                      Nous nous engageons à vous répondre dans les 24 heures ouvrées. 
                      Votre satisfaction est notre priorité.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                      Accompagnement personnalisé
                    </h3>
                    <p className="text-slate-600">
                      Chaque projet est unique. Nous vous proposons un accompagnement sur-mesure 
                      adapté à vos besoins spécifiques et à votre budget.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="h-96 bg-slate-200">
        <div className="w-full h-full flex items-center justify-center text-slate-500">
          <div className="text-center">
            <MapPin className="w-16 h-16 mx-auto mb-4" />
            <p className="text-lg font-medium">Carte interactive (intégration future)</p>
            <p className="text-sm">{companyInfo.address}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;